import { partition, sortBy } from 'lodash';
import { useQueryCampaignsByPlacementId } from 'modules/campaigns/editor/hooks';
import { GraphQLCampaign } from 'modules/campaigns/lib';
import { usePlacementsManageContext } from 'modules/placements/context/PlacementsManageContext';
import { useQueryPlacementById } from 'modules/placements/hooks/useQueryPlacementById';
import { FC, useEffect, useState } from 'react';
import { useUpdateDisplayPriorities } from '../hooks/useUpdateDisplayPriorities';
import { DisplayPriorityCampaign } from '../types';
import { DisplayPriorityContext } from './DisplayPriorityContext';
import { useFetchEmbeddedTemplatesByCampaigns } from '../hooks/useFetchEmbeddedTemplatesByCampaigns';

export const DisplayPriorityProvider: FC<
  React.PropsWithChildren<{ campaign?: Partial<GraphQLCampaign> }>
> = ({ campaign = {}, children }) => {
  const addHiddenFieldToCampaigns = (displayPriorityCampaigns: GraphQLCampaign[]) =>
    displayPriorityCampaigns.map((displayCampaign) => {
      return { ...displayCampaign, hidden: false };
    });
  const { displayPriorityTakeoverPlacementId } = usePlacementsManageContext();
  const isCampaignEditorView = !!campaign.id;

  const placementId = displayPriorityTakeoverPlacementId ?? campaign?.placementId;

  const { campaigns, loading: isLoadingCampaignsForPlacement } =
    useQueryCampaignsByPlacementId(placementId);
  const { placement } = useQueryPlacementById(placementId);
  const { updateDisplayPriorities } = useUpdateDisplayPriorities(placementId, campaign.id);
  const [campaignOrder, setCampaignOrder] = useState<DisplayPriorityCampaign[]>(
    addHiddenFieldToCampaigns(campaigns),
  );
  const { results: campaignsWithContent, isLoading: isLoadingCampaignsWithContent } =
    useFetchEmbeddedTemplatesByCampaigns(campaignOrder);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const initCampaignOrder = (campaignsList: DisplayPriorityCampaign[]) => {
    const sorted = sortBy(campaignsList, ['embeddedPriorityOrder']);

    if (!isCampaignEditorView || campaign.embeddedPriorityOrder) {
      setCampaignOrder(sorted);
    } else {
      const [currentCampaign, others] = partition(sorted, (c) => c.id === campaign.id);
      // If the current campaign doesn't have display priority default it to the top
      setCampaignOrder([...currentCampaign, ...others]);
    }
  };

  useEffect(() => {
    initCampaignOrder(addHiddenFieldToCampaigns(campaigns));
  }, [campaigns]);

  const handleSetOrder = (newCampaignOrder: DisplayPriorityCampaign[]) => {
    setCampaignOrder(newCampaignOrder);
    setIsDirty(true);
  };

  const handleReset = () => {
    initCampaignOrder(addHiddenFieldToCampaigns(campaigns));
    setIsDirty(false);
  };

  const handleDisplayPriorityUpdate = () => {
    const priorities = campaignOrder.map((c, index) => ({
      campaignId: c.id,
      displayPriority: index + 1,
    }));
    updateDisplayPriorities({
      variables: { priorities },
    });
    setIsDirty(false);
  };

  const handleShowCampaign = (index: number) => {
    const campaignOrderCopy = [...campaignOrder];
    const displayPriorityCampaign = campaignOrderCopy[index];
    const updatedDisplayCampaign = {
      ...displayPriorityCampaign,
      hidden: !displayPriorityCampaign.hidden,
    };
    campaignOrderCopy[index] = updatedDisplayCampaign;
    setCampaignOrder(campaignOrderCopy);
  };

  return (
    <DisplayPriorityContext.Provider
      value={{
        campaignsByPlacement: addHiddenFieldToCampaigns(campaigns),
        campaignPlacement: placement,
        campaignDisplayPriorityOrder: campaignOrder,
        setCampaignDisplayPriorityOrder: handleSetOrder,
        setShowCampaign: handleShowCampaign,
        isDirty,
        isCampaignEditorView,
        reset: handleReset,
        saveDisplayPriority: handleDisplayPriorityUpdate,
        campaignsWithContent,
        isLoadingCampaignsWithContent,
        isLoadingCampaignsForPlacement,
      }}
    >
      {children}
    </DisplayPriorityContext.Provider>
  );
};
